import React from 'react';
import useOperatingMarkets from '../../../hooks/useOperatingMarkets';
import DatePicker from '../DatePicker';
import TimePicker from '../TimePicker';

const minEventDate = () => {
  // API requires after:today — same-day events are not allowed
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const EventDetailsForm = ({
  bookingData,
  errors,
  onInputChange,
  requiredFields = [],
}) => {
  const { options: countryOptions, isLoading: marketsLoading, isEmpty } = useOperatingMarkets({
    bookableOnly: true,
  });
  const req = (field) => requiredFields.includes(field);
  const locationRequired = req('event_address') || req('event_location');
  const marketError =
    errors.eventCountry || (isEmpty ? 'No markets are open for bookings' : '');

  return (
    <div>
      <h2 className="marketing-display-md mb-5 text-m-ink">
        Event details
      </h2>
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <div>
          <label className="auth-field-label" htmlFor="booking-event-date">
            Event date <span className="text-red-500">*</span>
          </label>
          <DatePicker
            id="booking-event-date"
            value={bookingData.eventDate || ''}
            onChange={(value) => onInputChange('eventDate', value)}
            minDate={minEventDate()}
            allowPastDates={false}
            placeholder="Select date"
            required
            error={errors.eventDate}
            className="auth-input"
          />
        </div>

        <div>
          <label className="auth-field-label" htmlFor="booking-event-time">
            Event time <span className="text-red-500">*</span>
          </label>
          <TimePicker
            id="booking-event-time"
            value={bookingData.eventTime || ''}
            onChange={(value) => onInputChange('eventTime', value)}
            format="12"
            step={15}
            placeholder="Select time"
            required
            error={errors.eventTime}
            className="auth-input"
          />
        </div>

        <div>
          <label className="auth-field-label" htmlFor="booking-guest-count">
            Guest count
          </label>
          <input
            id="booking-guest-count"
            type="number"
            min="1"
            value={bookingData.guestCount || ''}
            onChange={(e) => onInputChange('guestCount', e.target.value)}
            placeholder="e.g. 120"
            className="auth-input"
          />
        </div>

        <div>
          <label className="auth-field-label" htmlFor="booking-event-type">
            Event type <span className="text-red-500">*</span>
          </label>
          <input
            id="booking-event-type"
            type="text"
            value={bookingData.eventType || ''}
            onChange={(e) => onInputChange('eventType', e.target.value)}
            placeholder="e.g., Wedding, Birthday, Corporate"
            required
            className="auth-input"
          />
          {errors.eventType && (
            <p className="mt-1 text-xs text-red-600">{errors.eventType}</p>
          )}
        </div>

        <div className="md:col-span-2">
          <label className="auth-field-label" htmlFor="booking-location">
            Event location {locationRequired && <span className="text-red-500">*</span>}
          </label>
          <input
            id="booking-location"
            type="text"
            value={bookingData.location || ''}
            onChange={(e) => onInputChange('location', e.target.value)}
            placeholder="Enter full event address"
            required={locationRequired}
            className="auth-input"
          />
          {errors.location && (
            <p className="mt-1 text-xs text-red-600">{errors.location}</p>
          )}
        </div>

        <div className="md:col-span-2">
          <label className="auth-field-label" htmlFor="booking-event-market">
            Event market {req('event_country') && <span className="text-red-500">*</span>}
          </label>
          <select
            id="booking-event-market"
            value={bookingData.eventCountry || ''}
            onChange={(e) => onInputChange('eventCountry', e.target.value)}
            disabled={marketsLoading || isEmpty}
            required={req('event_country')}
            className="auth-input"
          >
            <option value="">
              {marketsLoading ? 'Loading markets…' : 'Select market'}
            </option>
            {countryOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {marketError ? (
            <p className="mt-1 text-xs text-red-600">{marketError}</p>
          ) : (
            <p className="mt-1 text-xs text-m-ink-faint">
              Only markets the marketplace operates in.
            </p>
          )}
        </div>

        <div className="md:col-span-2">
          <label className="auth-field-label" htmlFor="booking-special-requests">
            Special requests
          </label>
          <textarea
            id="booking-special-requests"
            rows={4}
            value={bookingData.specialRequests || ''}
            onChange={(e) => onInputChange('specialRequests', e.target.value)}
            placeholder="Any special requirements or requests…"
            className="auth-textarea"
          />
        </div>
      </div>
    </div>
  );
};

export default EventDetailsForm;
